// PRD.275 — Elia AI Assistant Drawer (Phase 1, UI only).
//
// Drawer header (FR-5): optional back arrow (mobile), Elia avatar, name +
// subtitle, a more-menu placeholder (safe UI-only actions), and close.

import { useEffect, useRef, useState } from 'react';
import { ELIA_COLORS, ELIA_FONT } from './elia-theme';
import { EliaAvatar } from './EliaAvatar';
import { EliaArrowLeftIcon, EliaCloseIcon, EliaMoreIcon } from './EliaIcons';

function HeaderIconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{
        width: 32,
        height: 32,
        borderRadius: '9999px',
        border: 'none',
        background: 'transparent',
        color: ELIA_COLORS.textMuted,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
        padding: 0,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = ELIA_COLORS.neutralSurface;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

export function EliaAiHeader({
  showBack = false,
  onBack,
  onClose,
  onReset,
}: {
  showBack?: boolean;
  onBack?: () => void;
  onClose: () => void;
  onReset: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 12px 13px',
        borderBottom: `1px solid ${ELIA_COLORS.borderSubtle}`,
        fontFamily: ELIA_FONT,
      }}
    >
      {showBack ? (
        <HeaderIconButton label="Close Elia AI" onClick={onBack ?? onClose}>
          <EliaArrowLeftIcon size={16} />
        </HeaderIconButton>
      ) : null}

      <EliaAvatar size={36} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            fontWeight: 600,
            color: ELIA_COLORS.text,
            letterSpacing: '-0.15px',
            whiteSpace: 'nowrap',
          }}
        >
          Elia AI
        </p>
        <p
          style={{
            margin: 0,
            fontSize: 10,
            color: ELIA_COLORS.textFaint,
            whiteSpace: 'nowrap',
          }}
        >
          Your clinic assistant
        </p>
      </div>

      <div ref={menuRef} style={{ position: 'relative' }}>
        <HeaderIconButton label="More Elia AI options" onClick={() => setMenuOpen((v) => !v)}>
          <EliaMoreIcon size={16} />
        </HeaderIconButton>
        {menuOpen ? (
          <div
            role="menu"
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              right: 0,
              minWidth: 180,
              background: ELIA_COLORS.surface,
              border: `1px solid ${ELIA_COLORS.border}`,
              borderRadius: 10,
              boxShadow: '0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.1)',
              padding: 4,
              zIndex: 1,
            }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onReset();
                setMenuOpen(false);
              }}
              style={menuItemStyle}
            >
              Clear demo conversation
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
              title="Elia AI is your clinic assistant. This is a preview."
              style={menuItemStyle}
            >
              About Elia AI
            </button>
          </div>
        ) : null}
      </div>

      <HeaderIconButton label="Close Elia AI" onClick={onClose}>
        <EliaCloseIcon size={16} />
      </HeaderIconButton>
    </div>
  );
}

const menuItemStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'transparent',
  border: 'none',
  borderRadius: 8,
  padding: '8px 12px',
  fontFamily: ELIA_FONT,
  fontSize: 12,
  color: ELIA_COLORS.text,
  cursor: 'pointer',
};
